// =============================================================================
// FILE: packages/renderer/src/i18n/LocaleContext.tsx
// LAYER: B7 — React context + provider for per-user locale.
//
// Persists via IPC (locale:setPreference); falls back to localStorage
// in web preview. Missing translation keys fall back to English.
// =============================================================================

import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { call } from '../lib/api.js';
import { IPC } from '@bim/shared/constants/ipc.constants.js';
import { CATALOGUES, type LocaleCode } from './catalogues.js';

const STORAGE_KEY = 'bim.locale';

interface LocaleContextValue {
  locale: LocaleCode;
  setLocale: (l: LocaleCode) => Promise<void>;
  t: (key: string) => string;
  providerInfo: { providerKey: string; displayName: string; isLive: boolean } | null;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

function detectDefault(): LocaleCode {
  const stored = localStorage.getItem(STORAGE_KEY) as LocaleCode | null;
  if (stored && stored in CATALOGUES) return stored;
  const navLang = (navigator.language || 'en').slice(0, 2).toLowerCase();
  if (navLang === 'fr') return 'fr';
  if (navLang === 'es') return 'es';
  if (navLang === 'de') return 'de';
  if (navLang === 'fil' || navLang === 'tl') return 'fil';
  return 'en';
}

export function LocaleProvider({ children }: { children: ReactNode }): JSX.Element {
  const [locale, setLocaleState] = useState<LocaleCode>(detectDefault());
  const [providerInfo, setProviderInfo] = useState<LocaleContextValue['providerInfo']>(null);

  // Initial hydrate — pull stored preference from backend (overrides
  // browser default) + provider info.
  useEffect(() => {
    void (async () => {
      try {
        const pref = await call<Record<string, never>,
          { locale: LocaleCode } | null>(IPC.LOCALE_GET_PREFERENCE, {});
        if (pref && pref.locale && pref.locale in CATALOGUES) {
          setLocaleState(pref.locale);
        }
      } catch { /* swallow — keep detected default */ }
      try {
        const info = await call<Record<string, never>,
          { providerKey: string; displayName: string; isLive: boolean }>(
            IPC.LOCALE_PROVIDER_INFO, {});
        setProviderInfo(info);
      } catch { /* leave null */ }
    })();
  }, []);

  const setLocale = useCallback(async (l: LocaleCode) => {
    setLocaleState(l);
    localStorage.setItem(STORAGE_KEY, l);
    try {
      await call<{ locale: LocaleCode }, unknown>(IPC.LOCALE_SET_PREFERENCE, { locale: l });
    } catch { /* offline: localStorage persists */ }
  }, []);

  const t = useCallback((key: string) => {
    const lang = CATALOGUES[locale] ?? CATALOGUES.en;
    if (lang[key] !== undefined) return lang[key]!;
    const fallback = CATALOGUES.en[key];
    if (fallback === undefined) {
      // Debug warning — never blank.
      console.warn(`[B7] missing i18n key: ${key}`);
      return key;
    }
    return fallback;
  }, [locale]);

  const value = useMemo<LocaleContextValue>(
    () => ({ locale, setLocale, t, providerInfo }),
    [locale, setLocale, t, providerInfo],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used inside <LocaleProvider>');
  return ctx;
}
